import React from 'react';
import styled from 'styled-components';
import { MEDIA } from '@constants';
import { PageSlice, SliceInformationAccordionItem, SliceInformationAccordionDetails, AccordionData } from '@contracts';
import { getCommaSeparatedValues } from '@utils/helpers';
import Colors from '../Colors';
import Accordion from './Accordion';
import PillsGrid from './PillsGrid';
import { TextVariant, Typography } from './Typography';

interface Props {
  slice: PageSlice;
}

const InformationAccordion: React.FC<Props> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceInformationAccordionDetails;
  const sliceItems = slice.items as SliceInformationAccordionItem[];

  const transformedItems = sliceItems.map(
    (item): AccordionData => ({
      title: item.item_title?.text || '',
      contentText: item.item_description?.text || '',
      children: (
        <>
          {item.pill_elements && (
            <PillsGridWrapper>
              <Typography $variant={TextVariant.UpperText6}>{item.tags_description?.text ?? ''}</Typography>
              <PillsGrid pills={getCommaSeparatedValues(item.pill_elements ?? '')} textVariant={TextVariant.BodyRegularSM} />
            </PillsGridWrapper>
          )}
        </>
      )
    })
  );

  return (
    <Container>
      <TextWrapper>
        <Typography $variant={TextVariant.Heading3} $mobileVariant={TextVariant.Heading5}>
          {sliceDetails.heading?.text || ''}
        </Typography>
        <Typography $variant={TextVariant.BodyRegularLG}>{sliceDetails.subheading?.text || ''}</Typography>
      </TextWrapper>
      <AccordionWrapper>
        <Accordion items={transformedItems} isUsingArrows={true} />
      </AccordionWrapper>
    </Container>
  );
};

export default InformationAccordion;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  padding: 96px 100px;
  align-items: start;
  background: linear-gradient(180deg, #eae9f9 0%, #fdf3de 100%), #fffbe7;

  ${MEDIA.mobileM} {
    display: flex;
    flex-direction: column;
    padding: 56px 16px;
    align-items: flex-start;
    gap: 24px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 32px;
  width: 100%;

  ${MEDIA.mobileM} {
    gap: 16px;
  }
`;

const AccordionWrapper = styled.div`
  display: flex;
  padding: 0px 16px;
  flex-direction: column;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${Colors.paleGold50};
  background: ${Colors.cream};
`;

const PillsGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 0 0 0;
`;
